import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import clsx from "clsx";

import useLanguageChange from "../../hooks/useLanguageChange";
import { handleStartRecording, handleStopRecording, resetStartRecordingData, resetStopRecordingData } from "../../actions/recordingAction";
import {
    setRecordInProcess,
    setRecordingBtnEnabled,
    setRecordingErrorView,
    setRecordingInfoOnLoad,
    setRecordingON,
} from "../../reducers/recording-reducer";
import { sendRecordingError, sendStartRecordingMsg, sendStopRecordingMsg } from "../../shared/dataTrackFunction";
import {
    hideRecordingErrorView,
    isRecordingButtonAvailable,
    showCallRecordingMessage,
    showRecordingDisableBtn,
    showRecordingTooltip,
} from "../Recording/RecordingUtils";
import { LogFirebase } from "../../services/firestore";
import googleEvent from "../../services/googleEvent";
import { initialRecordingOff } from "../../shared/utility";
import { EVENT_NAME } from "../../constants/GTagConstants";

const STATUS = {
    REC_INITIALIZE: "1",
    REC_STRAT: "2",
};

const RecordingButton = () => {
    const dispatch = useDispatch();
    const { isRemoteParticipantJoined, currentCallBodyScreen } = useSelector((state) => state.commonReducer);
    const { recordingBtnEnabled, recordingON, startRecording, stopRecording, recordingInfoOnLoad } = useSelector((state) => state.recording);
    const localisedValues = useLanguageChange();

    const isButtonDisabled = useMemo(() => {
        return showRecordingDisableBtn(isRemoteParticipantJoined, recordingBtnEnabled, currentCallBodyScreen);
    }, [isRemoteParticipantJoined, recordingBtnEnabled, currentCallBodyScreen]);

    const recordingTooltip = useMemo(() => {
        return showRecordingTooltip(isRemoteParticipantJoined, recordingBtnEnabled, recordingON);
    }, [isRemoteParticipantJoined, recordingBtnEnabled, recordingON]);

    useEffect(() => {
        return () => {
            dispatch(resetStartRecordingData());
            dispatch(resetStopRecordingData());
        };
    }, []);

    const toggleRecording = () => {
        dispatch(setRecordingBtnEnabled(false));
        if (recordingON) {
            dispatch(handleStopRecording());
            googleEvent.logEvent(EVENT_NAME.STOPPED_RECORDING);
            LogFirebase("Recording", "agent_stopped_recording", "");
        } else {
            LogFirebase("Recording", "agent_started_recording", "");
            googleEvent.logEvent(EVENT_NAME.STARTED_RECORDING);
            sendStartRecordingMsg(STATUS.REC_INITIALIZE, "NA"); // roomname='NA'
            dispatch(setRecordInProcess(true));
            dispatch(handleStartRecording());
        }
    };

    useEffect(() => {
        if (startRecording?.data) {
            sendStartRecordingMsg(STATUS.REC_STRAT);
            dispatch(setRecordInProcess(false));
            dispatch(setRecordingON(true));
            dispatch(setRecordingInfoOnLoad(true));
            dispatch(setRecordingErrorView(false));
            dispatch(setRecordingBtnEnabled(true));
            if (initialRecordingOff() || recordingInfoOnLoad) {
                showCallRecordingMessage();
            }
        }
    }, [startRecording?.data]);

    useEffect(() => {
        if (startRecording?.error) {
            console.log(startRecording?.error);
            dispatch(setRecordingBtnEnabled(true));
            dispatch(setRecordInProcess(false));
            sendRecordingError(startRecording?.error);
            LogFirebase("Recording", "startRecordingRequestError", startRecording?.error);
        }
    }, [startRecording?.error]);

    useEffect(() => {
        if (stopRecording?.data) {
            sendStopRecordingMsg();
            LogFirebase("Recording", "stopRecordingRequestSuccess", stopRecording?.data);
            hideRecordingErrorView();
            dispatch(setRecordingBtnEnabled(true));
            dispatch(setRecordingON(false));
            dispatch(setRecordingInfoOnLoad(false));
            dispatch(setRecordInProcess(false));
        }
    }, [stopRecording?.data]);

    useEffect(() => {
        if (stopRecording?.error) {
            dispatch(setRecordingBtnEnabled(true));
            sendRecordingError(stopRecording?.error);
            LogFirebase("Recording", "stopRecordingRequestError", stopRecording?.error);
        }
    }, [stopRecording?.error]);

    return (
        <>
            {isRecordingButtonAvailable() && (
                <div className='action-button' data-testid='recordingButton'>
                    <div className='action-button-in custom-tooltip top-tooltip center-tooltip tooltip-auto-width'>
                        <button
                            className={clsx("call-action-btn", recordingON && !isButtonDisabled && "start-recording")}
                            disabled={isButtonDisabled}
                            onClick={() => toggleRecording()}
                            data-testid='record-btn'
                        >
                            <span className='action-button-icon'>
                                <span className='for-icon icon-recording'></span>
                            </span>
                            <span className='for-text'>{recordingON ? localisedValues?.stop_rec_label : localisedValues?.start_rec_label}</span>
                        </button>
                        {isRemoteParticipantJoined && (
                            <div className='custom-tooltip-content text-nowrap' data-testid='recording-tooltip'>
                                <div className='custom-tooltip-in'>
                                    <p>
                                        {recordingTooltip === "StartRec"
                                            ? localisedValues?.start_recording_tooltip_label
                                            : localisedValues?.stop_recording_btn}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default RecordingButton;
